import React, { useCallback } from 'react';
import { ExternalLink, Trash2, Wrench } from 'lucide-react';

import { renderToolStatusBadge, renderToolTypeBadge } from '@/pages/Tools/components/tool-badge';

import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Button } from '@/components/ui/button';

import { Permissions } from '@/constants';

import { getToolSummary } from '@/lib/tool';

import useAlertStore from '@/store/alert.store';
import type { AgentTool } from '@/types/agent.type';

type ToolRowProps = {
  tool: AgentTool;
  onOpenTool: (tool: AgentTool) => void;
  onUnlink: (toolId: string) => void;
  isUnlinking: boolean;
};

export const ToolRow = React.memo(function ToolRow({
  tool,
  onOpenTool,
  onUnlink,
  isUnlinking,
}: ToolRowProps) {
  const handleUnlink = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      useAlertStore.getState().openAlert({
        title: 'Unlink tool',
        description: `Are you sure you want to unlink "${tool.name}" from this agent? The tool will remain available in the tools page.`,
        confirmText: 'Unlink',
        onConfirm: () => onUnlink(tool.id),
        type: 'destructive',
      });
    },
    [onUnlink, tool.id, tool.name]
  );

  const handleOpen = useCallback(() => {
    onOpenTool(tool);
  }, [onOpenTool, tool]);

  return (
    <div className="rounded-lg border transition-colors hover:bg-muted/40">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
        <button
          type="button"
          onClick={handleOpen}
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Wrench className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="truncate text-sm font-medium text-foreground">{tool.name}</div>
              {renderToolTypeBadge(tool.type)}
              {renderToolStatusBadge(tool.status)}
            </div>
            <p className="mt-1 truncate text-xs text-muted-foreground">{getToolSummary(tool)}</p>
          </div>
        </button>

        <div className="flex items-center justify-end gap-2 sm:shrink-0">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleOpen}
            title="Open tool details"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>

          <ProtectedComponent requiredPermissions={[Permissions.AgentCreateUpdate]}>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 hover:border-destructive/20 hover:bg-destructive/10 hover:text-destructive"
              onClick={handleUnlink}
              disabled={isUnlinking}
              title="Unlink tool"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </ProtectedComponent>
        </div>
      </div>
    </div>
  );
});
